import { supabase } from "@/integrations/supabase/client";

/**
 * Serviço centralizado para operações com cupons de assinatura
 * Contém funções auxiliares reutilizáveis em todo o projeto
 * 
 * Este serviço centraliza toda a lógica de:
 * - Normalização de códigos de cupom
 * - Resolução de IDs de planos (slug -> UUID)
 * - Cálculo de descontos
 * - Validação e aplicação de cupons
 * - Mapeamento de mensagens de erro
 */

/**
 * Normaliza código do cupom para formato padronizado
 * - Converte para maiúsculas
 * - Remove acentos
 * - Substitui espaços/underscores por hífen
 * - Remove caracteres especiais
 * - Remove hífens duplicados
 * 
 * @param input Código bruto do cupom (ex: "black friday", "PROMO_2024")
 * @returns Código normalizado (ex: "BLACK-FRIDAY", "PROMO-2024")
 * 
 * @example
 * normalizeCouponCode("Black Friday") // "BLACK-FRIDAY"
 * normalizeCouponCode("PROMOÇÃO_2024") // "PROMOCAO-2024"
 */
export function normalizeCouponCode(input: string): string {
  return input
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // Remove acentos
    .replace(/[\s_/]+/g, "-") // Substitui espaços/underscore por hífen
    .replace(/[^A-Za-z0-9-]/g, "") // Remove caracteres especiais
    .replace(/-+/g, "-") // Remove hífens duplicados
    .toUpperCase()
    .trim();
}

/**
 * Verifica se uma string é um UUID válido v4
 * 
 * @param value String a ser verificada
 * @returns true se for UUID válido, false caso contrário
 * 
 * @example
 * isUuid("550e8400-e29b-41d4-a716-446655440000") // true
 * isUuid("plus") // false
 */
export function isUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}

/**
 * Resolve ID do plano a partir de slug legado ou UUID
 * 
 * Suporta conversão de slugs legados para UUID:
 * - "plus" → plano sem Chatwoot e sem IA
 * - "plus_whats" → plano com Chatwoot, sem IA
 * - "plus_whats_ia" → plano com Chatwoot e IA
 * 
 * Se já for UUID, retorna direto sem consultar banco
 * 
 * @param planParam Slug ou UUID do plano
 * @returns Promise<string> UUID do plano
 * 
 * @example
 * await resolvePlanId("plus") // "550e8400-e29b-41d4-a716-446655440000"
 * await resolvePlanId("550e8400-e29b-41d4-a716-446655440000") // "550e8400-e29b-41d4-a716-446655440000"
 */
export async function resolvePlanId(planParam: string): Promise<string> {
  // Se já é UUID, retornar direto
  if (isUuid(planParam)) {
    return planParam;
  }
  
  // Mapear slug para características do plano
  const slug = String(planParam || '').toLowerCase();
  let includes_chatwoot: boolean | null = null;
  let includes_ai: boolean | null = null;
  
  if (slug === 'plus') {
    includes_chatwoot = false;
    includes_ai = false;
  } else if (slug === 'plus_whats') {
    includes_chatwoot = true;
    includes_ai = false;
  } else if (slug === 'plus_whats_ia') {
    includes_chatwoot = true;
    includes_ai = true;
  } else {
    // Slug desconhecido, retornar como está (pode ser UUID malformado ou slug customizado)
    return planParam;
  }
  
  // Buscar plano no banco
  const { data } = await supabase
    .from('subscription_plans')
    .select('id')
    .eq('includes_chatwoot', includes_chatwoot)
    .eq('includes_ai', includes_ai)
    .limit(1)
    .maybeSingle();
  
  return data?.id ?? planParam;
}

/**
 * Calcula valor final com desconto aplicado
 * 
 * Suporta 4 tipos de desconto:
 * - "full": desconto total (100%), retorna 0
 * - "none": sem desconto, retorna preço original
 * - "percentage": desconto percentual (0-100%)
 * - "fixed": desconto por valor fixo em reais
 * 
 * @param price Preço original do plano em reais
 * @param discountType Tipo de desconto (none, full, percentage, fixed)
 * @param discountValue Valor do desconto (% ou R$)
 * @returns Valor final com desconto aplicado, arredondado para 2 casas decimais
 * 
 * @example
 * calculateDiscountedAmount(100, "percentage", 20) // 80.00
 * calculateDiscountedAmount(100, "fixed", 30) // 70.00
 * calculateDiscountedAmount(100, "full", null) // 0.00
 * calculateDiscountedAmount(100, "none", null) // 100.00
 */
export function calculateDiscountedAmount(
  price: number,
  discountType: string,
  discountValue: number | null
): number {
  // Desconto total (100%)
  if (discountType === "full") {
    return 0;
  }
  
  // Sem desconto
  if (discountType === "none" || !discountValue) {
    return Number(price);
  }
  
  // Desconto por porcentagem (limita entre 0-100%)
  if (discountType === "percentage") {
    const percentage = Math.max(0, Math.min(100, Number(discountValue)));
    const discounted = price * (1 - percentage / 100);
    return Math.round(discounted * 100) / 100;
  }
  
  // Desconto por valor fixo (não pode ser negativo)
  if (discountType === "fixed") {
    const discounted = Math.max(0, Number(price) - Number(discountValue));
    return Math.round(discounted * 100) / 100;
  }
  
  // Tipo desconhecido, retorna preço original
  return Number(price);
}

/**
 * Determina se o cupom requer pagamento
 * 
 * Cupons especiais (cortesia, testers, promoção) não requerem pagamento
 * Cupons regulares com desconto total também não requerem pagamento
 * Demais casos requerem pagamento
 * 
 * @param activationType Tipo de ativação do cupom (regular, cortesia, testers, promocao)
 * @param discountType Tipo de desconto (none, full, percentage, fixed)
 * @returns true se requer pagamento, false se isento
 * 
 * @example
 * mustPayWithCoupon("cortesia", "none") // false (cupom especial)
 * mustPayWithCoupon("regular", "full") // false (desconto total)
 * mustPayWithCoupon("regular", "percentage") // true (desconto parcial)
 */
export function mustPayWithCoupon(activationType: string, discountType: string): boolean {
  // Cupons especiais não requerem pagamento
  if (activationType !== "regular") {
    return false;
  }
  
  // Desconto total isenta pagamento
  return discountType !== "full";
}

/**
 * Mapeia razões de invalidação de cupom para mensagens amigáveis em português
 * 
 * @param reason Código da razão retornado pela API de validação
 * @returns Mensagem amigável em português
 * 
 * @example
 * getCouponErrorMessage("not_found") // "Cupom não encontrado"
 * getCouponErrorMessage("expired") // "Cupom inválido" (fallback)
 */
export function getCouponErrorMessage(reason: string): string {
  const errorMap: Record<string, string> = {
    not_found: 'Cupom não encontrado',
    inactive: 'Cupom inativo',
    before_start: 'Cupom ainda não está válido',
    after_end: 'Cupom expirado',
    plan_not_allowed: 'Cupom não permitido para este plano',
    total_limit_exceeded: 'Limite total do cupom excedido',
    per_user_limit_exceeded: 'Você já usou este cupom o máximo de vezes permitido'
  };
  
  return errorMap[reason] || 'Cupom inválido';
}

/**
 * Valida cupom via edge function de validação
 * 
 * Chama a edge function `/functions/v1/subscription_coupons` com action=validate
 * Verifica status, janela de validade, planos permitidos e limites de uso
 * 
 * @param code Código do cupom (será normalizado automaticamente)
 * @param planId ID do plano (slug ou UUID)
 * @param tenantId ID do tenant
 * @param userId ID do usuário
 * @returns Promise com dados de validação do cupom
 * @throws Error se validação falhar ou cupom for inválido
 * 
 * @example
 * const result = await validateCoupon("BLACK-FRIDAY", "plus", tenantId, userId);
 * // { is_valid: true, discounted_amount: 39.90, must_pay: true, ... }
 */
export async function validateCoupon(
  code: string,
  planId: string,
  tenantId: string,
  userId: string
) {
  const normalizedCode = normalizeCouponCode(code);
  
  const { data, error } = await supabase.functions.invoke('subscription_coupons', {
    body: { 
      action: 'validate', 
      tenant_id: tenantId, 
      user_id: userId, 
      plan_id: planId, 
      code: normalizedCode 
    }
  });
  
  if (error) {
    throw new Error(error.message || 'Erro ao validar cupom');
  }
  
  if (!data?.is_valid) {
    throw new Error(getCouponErrorMessage(data?.reason));
  }
  
  return data;
}

/**
 * Aplica cupom que isenta pagamento (cortesia, testers, promoção ou desconto total)
 * 
 * Chama a edge function `/functions/v1/subscription_coupons` com action=apply
 * Cria transação com payment_method=coupon e ativa plano por período determinado
 * 
 * @param code Código do cupom (será normalizado automaticamente)
 * @param planId ID do plano (slug ou UUID)
 * @param tenantId ID do tenant
 * @param userId ID do usuário
 * @returns Promise com resultado da aplicação (subscription_transaction_id)
 * @throws Error se aplicação falhar
 * 
 * @example
 * const result = await applyCouponWithoutPayment("CORTESIA-2024", "plus", tenantId, userId);
 * // { success: true, subscription_transaction_id: "..." }
 */
export async function applyCouponWithoutPayment(
  code: string,
  planId: string,
  tenantId: string,
  userId: string
) {
  const normalizedCode = normalizeCouponCode(code);
  
  const { data, error } = await supabase.functions.invoke('subscription_coupons', {
    body: { 
      action: 'apply', 
      tenant_id: tenantId, 
      user_id: userId, 
      plan_id: planId, 
      code: normalizedCode 
    }
  });
  
  if (error) {
    throw new Error(error.message || 'Erro ao aplicar cupom');
  }
  
  if (!data?.success) {
    throw new Error(data?.error || 'Falha ao aplicar cupom');
  }
  
  return data;
}
